import {createCmsApiProvider} from './providers/cmsApiProvider'
import {createDisabledProvider} from './providers/disabledProvider'

export interface PageContext {
  pageTitle?: string
  locale?: string // e.g. 'en' | 'ne' — used by capable providers (Phase 2)
}

export type Confidence = 'low' | 'med' | 'high'

export interface Suggestion {
  text: string
  confidence: Confidence
  provider: string
}

export interface ProviderCapabilities {
  altText: boolean
  linkText: boolean
  headerLabel: boolean   // table column header text
  tableCaption: boolean  // table caption / "table name"
  headingHint: boolean
  ariaLabel: boolean     // aria-label for icon-only controls / regions
}

export interface A11yAiProvider {
  readonly id: string
  readonly capabilities: ProviderCapabilities
  suggestAltText(image: Blob | string, ctx?: PageContext): Promise<Suggestion>
  suggestLinkText(href: string, surroundingText: string, ctx?: PageContext): Promise<Suggestion>
  suggestHeaderLabel(columnSamples: string[], ctx?: PageContext): Promise<Suggestion>
  suggestTableCaption(cellSamples: string[], ctx?: PageContext): Promise<Suggestion>
  suggestHeadingHint(sectionText: string, ctx?: PageContext): Promise<Suggestion>
  suggestAriaLabel(elementDescription: string, ctx?: PageContext): Promise<Suggestion>
}

const ALT_MAX = 125

export function buildAltTextFromCaption(caption: string, _ctx?: PageContext): string {
  let text = (caption || '').trim().replace(/\s+/g, ' ')
  if (!text) return ''
  text = text.charAt(0).toUpperCase() + text.slice(1)
  text = text.replace(/\.+$/, '')
  if (text.length > ALT_MAX) {
    text = text.slice(0, ALT_MAX).replace(/\s+\S*$/, '').trim()
  }
  return text
}

export function deriveLinkText(href: string, surroundingText: string): string {
  try {
    const url = new URL(href, 'https://placeholder.invalid')
    const seg = url.pathname.split('/').filter(Boolean).pop()
    // If there's a path segment, use it; otherwise use hostname
    const isPathSegment = !!seg
    const source = seg || url.hostname.replace(/^www\./, '')
    const humanized = decodeURIComponent(source)
      .replace(/\.(html?|php|aspx?|pdf|docx?)$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim()
    if (humanized) {
      // Only capitalize if it's a path segment, not a hostname
      if (isPathSegment) {
        return humanized.charAt(0).toUpperCase() + humanized.slice(1)
      }
      return humanized
    }
  } catch {
    // fall through to surrounding text
  }
  const fallback = surroundingText.trim().slice(0, 60)
  return fallback || 'View page'
}

export function isAiEnabled(): boolean {
  const flag = (process.env.REACT_APP_AI_ENABLED ?? '').toLowerCase()
  if (flag !== 'true') return false
  // Only the CMS backend proxy is supported: AI provider credentials must never ship to the browser.
  // A leftover legacy value such as 'claude' simply keeps AI switched off.
  const provider = (process.env.REACT_APP_A11Y_AI_PROVIDER || 'cms').toLowerCase()
  return provider === 'cms'
}

let cached: A11yAiProvider | null = null

export function getA11yAiProvider(): A11yAiProvider {
  if (cached) return cached
  if (!isAiEnabled()) {
    cached = createDisabledProvider('disabled')
    return cached
  }
  const id = (process.env.REACT_APP_A11Y_AI_PROVIDER || 'cms').toLowerCase()
  switch (id) {
    case 'selfhosted':
    case 'cloud':
      cached = createDisabledProvider(id) // Phase 2 replaces these with real implementations
      break
    case 'cms':
    default:
      cached = createCmsApiProvider()
  }
  return cached
}

/** Test-only: clears the memoized provider so env changes take effect. */
export function __resetA11yAiProviderCache(): void {
  cached = null
}
